import {
    useContext,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    View
} from "react-native";
import type IPieChartProps from "./type";
import type {
    PieChartLegendItem,
    PieChartSlice
} from "./type";
import stylesheet, {
    PIE_CHART_MAX_SLICES,
    getPieChartMeasures,
    useStyles
} from "./stylesheet";
import PieChartLegend from "./components/legend";
import PieChartSlicePart from "./components/slice";
import {
    PIE_CHART_LABEL_SAMPLE,
    PIE_CHART_CENTER_SHIFT,
    PIE_CHART_MAX_MEMBERS,
    getPieChartHitAngle,
    getPieChartGeometry,
    getPieChartAngles,
    getPieChartLabels,
    getPieChartSlices,
    getPieChartPoint,
    getPieChartFit
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import Chart, {
    getChartStaggerProgress,
    useChartValueTransition,
    useChartModel
} from "../chart";
import type {
    ChartTooltipContent,
    ChartPlotLayout,
    ChartDataRow
} from "../chart/type";
import {
    Text as SvgText,
    G
} from "react-native-svg";
import {
    formatPercent,
    formatNumber
} from "../../helpers/locale";
import {
    getChartTextWidth
} from "../chart/util";
import {
    ChartContext
} from "../chart/context";
import ChartPatternDefs from "../chart/components/pattern";

const PieChart: FC<IPieChartProps> = ({
    isAnimatedOnMount: isAnimatedOnMountProp,
    customLocalize: customLocalizeProp,
    valueFormatter: valueFormatterProp,
    maxSlices = PIE_CHART_MAX_SLICES,
    centerTitle: centerTitleProp,
    centerValue: centerValueProp,
    customTheme: customThemeProp,
    otherTitle: otherTitleProp,
    activeKey: activeKeyProp,
    labelBehaviour = "none",
    isShowLegend = true,
    plotSize = "small",
    variant = "donut",
    tooltipBehaviour,
    onActiveChange,
    categoryTitle,
    renderTooltip,
    categoryKey,
    valueKey,
    summary,
    style,
    data
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const customLocalize = customLocalizeProp ?? context?.customLocalize;

    const {
        shapeConfigs,
        typography,
        durations,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        rootWidth,
        setRootWidth
    ] = useState<number>(0);

    const [
        legendKey,
        setLegendKey
    ] = useState<string | undefined>(undefined);

    const [
        plotKey,
        setPlotKey
    ] = useState<string | undefined>(undefined);

    const config = context?.config;

    const isPatterned = context?.isPatterned ?? false;

    const otherTitle = otherTitleProp ?? localize("chart-other");

    const centerTitle = centerTitleProp ?? localize("chart-total");

    const valueFormatter = valueFormatterProp ?? ((value: number) => formatNumber({
        options: {
            maximumFractionDigits: 2
        },
        locale: activeLocale,
        value
    }));

    const slices = useMemo(() => getPieChartSlices({
        categoryKey,
        isPatterned,
        otherTitle,
        maxSlices,
        valueKey,
        colors,
        config,
        data
    }), [
        isPatterned,
        categoryKey,
        otherTitle,
        maxSlices,
        valueKey,
        config,
        colors,
        data
    ]);

    const rows = useMemo((): Array<ChartDataRow> => slices.map((slice) => ({
        [categoryKey]: slice.key,
        [valueKey]: slice.value
    })), [
        categoryKey,
        valueKey,
        slices
    ]);

    const seriesKeys = useMemo(() => [
        valueKey
    ], [
        valueKey
    ]);

    const shapeKey = slices.map((slice) => `${slice.key}:${slice.colorIndex}`).join("|");

    const transition = useChartValueTransition({
        values: slices.map((slice) => slice.value),
        customTheme,
        shapeKey
    });

    const structureRef = useRef<{ slices: Array<PieChartSlice>; key: string }>({
        key: shapeKey,
        slices
    });

    const previousSlicesRef = useRef<Array<PieChartSlice> | undefined>(undefined);

    if(structureRef.current.key !== shapeKey) {
        previousSlicesRef.current = structureRef.current.slices;
    }

    structureRef.current = {
        key: shapeKey,
        slices
    };

    const getSliceTitle = (key: string) => slices.find((slice) => slice.key === key)?.title ?? key;

    useChartModel({
        model: {
            categoryFormatter: (value) => getSliceTitle(String(value)),
            valueFormatter,
            categoryTitle,
            categoryKey,
            kind: "pie",
            data: rows,
            seriesKeys
        }
    });

    const isControlled = activeKeyProp !== undefined;

    const activeKey = isControlled ? activeKeyProp ?? undefined : legendKey ?? plotKey;

    const activeSliceIndex = slices.findIndex((slice) => slice.key === activeKey);

    const plotActiveIndex = isControlled ? (activeSliceIndex < 0 ? null : activeSliceIndex) : undefined;

    const measures = getPieChartMeasures({
        shapeConfigs,
        typography,
        borders,
        spaces
    });

    const isLabelled = variant === "pie" && labelBehaviour === "share";

    const {
        lineHeight: labelLineHeight,
        fontSize: labelFontSize
    } = measures.labelTypography;

    const labelWidth = getChartTextWidth({
        text: PIE_CHART_LABEL_SAMPLE,
        fontSize: labelFontSize
    });

    const labelOffset = measures.lift + spaces.spacingXs;

    const reserve = isLabelled ? labelOffset + labelLineHeight : measures.lift;

    const reserveX = isLabelled ? labelOffset + labelWidth : measures.lift;

    const plotHeight = shapeConfigs.chartHeight[plotSize];

    const isBeside = isShowLegend && slices.length > 0 && rootWidth >= plotHeight * 2 + spaces.spacingMd;

    const {
        legend: legendDynamicStyle,
        plot: plotDynamicStyle,
        root: rootDynamicStyle,
        label: labelColor,
        value: valueColor
    } = useStyles({
        plotHeight,
        isBeside,
        colors,
        spaces
    });

    const total = slices.reduce((sum, slice) => sum + slice.value, 0);

    const getShare = (share: number) => formatPercent({
        options: {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        },
        locale: activeLocale,
        value: share
    });

    const getLabelShare = (share: number) => formatPercent({
        options: {
            maximumFractionDigits: 0
        },
        locale: activeLocale,
        value: share
    });

    const angles = getPieChartAngles(transition.values);

    const shares = getPieChartAngles(slices.map((slice) => slice.value));

    const legendItems = slices.map((slice, index): PieChartLegendItem => ({
        share: getShare(shares[index]?.share ?? 0),
        value: valueFormatter(slice.value),
        colorIndex: slice.colorIndex,
        pattern: slice.pattern,
        title: slice.title,
        key: slice.key
    }));

    const valueWidth = Math.max(0, ...legendItems.map((item) => getChartTextWidth({
        fontSize: labelFontSize,
        text: item.value
    })));

    const shareWidth = Math.max(0, ...legendItems.map((item) => getChartTextWidth({
        fontSize: labelFontSize,
        text: item.share
    })));

    const getGeometry = ({
        height,
        width
    }: Pick<ChartPlotLayout, "height" | "width">) => getPieChartGeometry({
        measures,
        reserveX,
        reserve,
        variant,
        height,
        width
    });

    const handleActiveChange = (key: string | undefined) => {
        onActiveChange?.(key);
    };

    const handlePlotActiveChange = (index: number | undefined) => {
        const key = index === undefined ? undefined : slices[index]?.key;

        setPlotKey(key);

        handleActiveChange(key);
    };

    const handleLegendActiveChange = (key: string | undefined) => {
        setLegendKey(key);

        handleActiveChange(key);
    };

    const handleLayout = (event: LayoutChangeEvent) => {
        setRootWidth(event.nativeEvent.layout.width);
    };

    const getSliceText = (index: number) => {
        const slice = slices[index];

        return slice ? `${slice.title} ${valueFormatter(slice.value)} · ${getShare(shares[index]?.share ?? 0)}` : "";
    };

    const isCenterReading = variant === "donut" && isShowLegend;

    const getTooltip = (index: number): ChartTooltipContent | undefined => {
        const slice = slices[index];

        if(!slice || isCenterReading) {
            return undefined;
        }

        const members = slice.members.slice(0, PIE_CHART_MAX_MEMBERS);

        const restCount = slice.members.length - members.length;

        const restValue = slice.members.slice(PIE_CHART_MAX_MEMBERS).reduce((sum, member) => sum + member.value, 0);

        return {
            rows: [
                {
                    value: `${valueFormatter(slice.value)} · ${getShare(shares[index]?.share ?? 0)}`,
                    colorIndex: slice.colorIndex,
                    title: slice.title,
                    shape: "square",
                    key: slice.key
                },
                ...members.map((member, memberIndex) => ({
                    value: valueFormatter(member.value),
                    key: `${slice.key}-${memberIndex}`,
                    colorIndex: slice.colorIndex,
                    shape: "square" as const,
                    title: member.title
                })),
                ...restCount > 0 ? [
                    {
                        value: valueFormatter(restValue),
                        colorIndex: slice.colorIndex,
                        key: `${slice.key}-rest`,
                        shape: "square" as const,
                        title: `+${restCount}`
                    }
                ] : []
            ],
            title: categoryTitle ?? ""
        };
    };

    const getHighest = (): string => {
        const highestIndex = slices.reduce((best, slice, index) => slice.value > (slices[best]?.value ?? 0) ? index : best, 0);

        const highest = slices[highestIndex];

        if(!highest) {
            return "";
        }

        return localize("chart-highest-value", [
            highest.title,
            getShare(shares[highestIndex]?.share ?? 0)
        ]);
    };

    const currentSummary = summary ?? [
        context?.title,
        context?.description,
        `${centerTitle} ${centerValueProp ?? valueFormatter(total)}`,
        getHighest()
    ].filter(Boolean).join(". ");

    const getIndex: Parameters<typeof Chart.Plot>[0]["getIndex"] = (point, layout) => {
        const angle = getPieChartHitAngle({
            geometry: getGeometry(layout),
            isRTL: layout.isRTL,
            lift: measures.lift,
            x: point.x,
            y: point.y
        });

        if(angle === undefined) {
            return undefined;
        }

        const index = angles.findIndex((item) => angle >= item.start && angle < item.end);

        return index < 0 ? undefined : index;
    };

    const getAnchor: Parameters<typeof Chart.Plot>[0]["getAnchor"] = (index, _seriesIndex, layout) => {
        const angle = angles[index];

        if(!angle) {
            return undefined;
        }

        const geometry = getGeometry(layout);

        return getPieChartPoint({
            radius: (geometry.inner + geometry.outer) / 2,
            isRTL: layout.isRTL,
            angle: angle.mid,
            geometry
        });
    };

    const patternID = `${context?.baseID ?? "chart"}-pattern`;

    const renderLayer = ({
        isInteractive,
        layerAngles,
        layerSlices,
        geometry,
        opacity,
        layout,
        sweep,
        id
    }: {
        layerAngles: ReturnType<typeof getPieChartAngles>;
        geometry: ReturnType<typeof getGeometry>;
        layerSlices: Array<PieChartSlice>;
        layout: ChartPlotLayout;
        isInteractive: boolean;
        opacity: number;
        sweep: number;
        id: string;
    }) => {
        const order = layerSlices.map((_, index) => index).sort((first, second) => Number(first === activeSliceIndex && isInteractive) - Number(second === activeSliceIndex && isInteractive));

        return <G
            key={id}
        >
            {
                order.map((index) => {
                    const slice = layerSlices[index];

                    const angle = layerAngles[index];

                    if(!slice || !angle) {
                        return null;
                    }

                    return <PieChartSlicePart
                        isActive={isInteractive && index === activeSliceIndex}
                        surfaceColor={layout.surfaceColor}
                        start={angle.start * sweep}
                        key={`${id}-${slice.key}`}
                        customTheme={customTheme}
                        end={angle.end * sweep}
                        pattern={slice.pattern}
                        patternID={patternID}
                        isRTL={layout.isRTL}
                        lift={measures.lift}
                        color={slice.color}
                        geometry={geometry}
                        gap={measures.gap}
                        opacity={opacity}
                    />;
                })
            }
        </G>;
    };

    const renderCenter = ({
        geometry,
        sweep
    }: {
        geometry: ReturnType<typeof getGeometry>;
        sweep: number;
    }) => {
        const activeSlice = isCenterReading ? slices[activeSliceIndex] : undefined;

        const activeValue = activeSlice ? getShare(shares[activeSliceIndex]?.share ?? 0) : undefined;

        const titleText = activeSlice?.title ?? centerTitle;

        const finalValue = activeValue ?? centerValueProp ?? valueFormatter(total);

        const fit = getPieChartFit({
            valueText: finalValue,
            typography,
            titleText,
            geometry,
            measures
        });

        if(variant !== "donut" || !fit) {
            return null;
        }

        const {
            valueTypography,
            labelTypography
        } = fit;

        const centerHeight = valueTypography.lineHeight + (fit.isTitleVisible ? labelTypography.lineHeight : 0);

        const valueY = geometry.cy - centerHeight / 2 + valueTypography.lineHeight / 2 + valueTypography.fontSize * PIE_CHART_CENTER_SHIFT;

        const getValueText = (values: Array<number>) => valueFormatter(values.reduce((sum, value) => sum + value, 0) * sweep);

        const renderValue = (text: string, opacity: number, key: string) => <SvgText
            fontFamily={valueTypography.fontFamily}
            fontWeight={valueTypography.fontWeight}
            fontSize={valueTypography.fontSize}
            textAnchor="middle"
            opacity={opacity}
            fill={valueColor}
            x={geometry.cx}
            y={valueY}
            key={key}
        >
            {text}
        </SvgText>;

        return <G
            aria-hidden={true}
        >
            {
                activeValue !== undefined || centerValueProp !== undefined ?
                    renderValue(finalValue, 1, "value")
                :
                    <>
                        {renderValue(getValueText(transition.values), transition.opacity, "value")}
                        {
                            transition.previous ?
                                renderValue(getValueText(transition.previous.values), transition.previous.opacity, "previous")
                            :
                                null
                        }
                    </>
            }
            {
                fit.isTitleVisible ?
                    <SvgText
                        y={geometry.cy + centerHeight / 2 - labelTypography.lineHeight / 2 + labelTypography.fontSize * PIE_CHART_CENTER_SHIFT}
                        fontFamily={labelTypography.fontFamily}
                        fontWeight={labelTypography.fontWeight}
                        fontSize={labelTypography.fontSize}
                        textAnchor="middle"
                        fill={labelColor}
                        x={geometry.cx}
                    >
                        {titleText}
                    </SvgText>
                :
                    null
            }
        </G>;
    };

    const renderLabels = ({
        geometry,
        layout,
        sweep
    }: {
        geometry: ReturnType<typeof getGeometry>;
        layout: ChartPlotLayout;
        sweep: number;
    }) => {
        if(!isLabelled) {
            return null;
        }

        const labels = getPieChartLabels({
            labelTypography: measures.labelTypography,
            getText: getLabelShare,
            height: layout.height,
            offset: labelOffset,
            isRTL: layout.isRTL,
            width: layout.width,
            geometry,
            angles
        });

        return <G
            opacity={sweep * transition.opacity}
            aria-hidden={true}
        >
            {
                labels.map((label) => <SvgText
                    fontFamily={measures.labelTypography.fontFamily}
                    fontWeight={measures.labelTypography.fontWeight}
                    key={slices[label.index]?.key ?? label.index}
                    fontSize={measures.labelTypography.fontSize}
                    textAnchor={label.anchor}
                    fill={valueColor}
                    x={label.x}
                    y={label.y}
                >
                    {label.text}
                </SvgText>)
            }
        </G>;
    };

    const renderPlot = (layout: ChartPlotLayout) => {
        const geometry = getGeometry(layout);

        const sweep = getChartStaggerProgress({
            progress: layout.entrance.progress,
            stagger: durations.stagger,
            duration: durations.long,
            index: 0,
            count: 1
        });

        const previousSlices = previousSlicesRef.current;

        const previous = transition.previous;

        const isPreviousVisible = Boolean(previous) && previousSlices !== undefined && previous?.values.length === previousSlices.length;

        return <>
            <ChartPatternDefs
                patterns={[
                    ...slices.map((slice) => slice.pattern),
                    ...isPreviousVisible ? previousSlices?.map((slice) => slice.pattern) ?? [] : []
                ]}
                customTheme={customTheme}
                id={patternID}
            />
            {
                isPreviousVisible && previous && previousSlices ? renderLayer({
                    layerAngles: getPieChartAngles(previous.values),
                    layerSlices: previousSlices,
                    opacity: previous.opacity,
                    isInteractive: false,
                    id: "previous",
                    geometry,
                    layout,
                    sweep
                }) : null
            }
            {renderLayer({
                opacity: transition.opacity,
                isInteractive: true,
                layerAngles: angles,
                layerSlices: slices,
                id: "current",
                geometry,
                layout,
                sweep
            })}
            {renderLabels({
                geometry,
                layout,
                sweep
            })}
            {renderCenter({
                geometry,
                sweep
            })}
        </>;
    };

    return <View
        onLayout={handleLayout}
        style={[
            style,
            rootDynamicStyle
        ]}
    >
        <View
            style={[
                stylesheet.plot,
                plotDynamicStyle
            ]}
        >
            <Chart.Plot
                isAnimatedOnMount={isAnimatedOnMountProp}
                onActiveChange={handlePlotActiveChange}
                tooltipBehaviour={tooltipBehaviour}
                customLocalize={customLocalize}
                activeIndex={plotActiveIndex}
                renderTooltip={renderTooltip}
                style={stylesheet.container}
                pointCount={slices.length}
                customTheme={customTheme}
                getReading={getSliceText}
                summary={currentSummary}
                getTooltip={getTooltip}
                getAnchor={getAnchor}
                plotSize={plotSize}
                getIndex={getIndex}
            >
                {renderPlot}
            </Chart.Plot>
        </View>
        {
            isShowLegend && legendItems.length ?
                <PieChartLegend
                    accessibilityLabel={localize("chart-series-shares")}
                    onActiveChange={handleLegendActiveChange}
                    style={legendDynamicStyle}
                    customTheme={customTheme}
                    valueWidth={valueWidth}
                    shareWidth={shareWidth}
                    activeKey={activeKey}
                    items={legendItems}
                />
            :
                null
        }
    </View>;
};
export default PieChart;
